// ai-client.ts — provider-agnostic AI client interface + shared types.
// Features (2.1+) depend on this interface only; never on the proxy directly.
// Provider impl: proxy-provider.ts (shared-key Supabase Edge Function).

// ── Message types ─────────────────────────────────────────────────────────────

export type AiRole = "user" | "model";

export interface AiPart {
  text?: string;
  /** P4-1: multimodal inline image part for receipt OCR. `data` = base64 (no data: prefix). */
  inlineData?: { mimeType: string; data: string };
}

export interface AiMessage {
  role: AiRole;
  parts: AiPart[];
}

// ── Tool / function-calling types (Phase 2.5) ────────────────────────────────

/** Placeholder type; Phase 2.5 will flesh this out for function-calling. */
export interface AiToolDef {
  name: string;
  description: string;
  /** JSON Schema object for the tool parameters. */
  parameters: Record<string, unknown>;
}

/** A single tool declaration for function-calling (Gemini functionDeclarations item). */
export interface AiToolDecl {
  name: string;
  description: string;
  /** JSON-Schema subset: type/properties/required/enum/items/description ONLY.
   *  NEVER additionalProperties or minimum/maximum (Gemini returns 400). */
  parameters: Record<string, unknown>;
}

/** A tool call emitted by the model in a response part. */
export interface AiToolCall {
  name: string;
  args: Record<string, unknown>;
  /** Gemini 3 "thinking" signature on the functionCall part. MUST be echoed back
   *  verbatim when resending the model turn, or Gemini returns 400. */
  thoughtSignature?: string;
}

/** A single part within an AiContent turn; exactly one field is set per variant. */
export type AiContentPart =
  | { text: string; functionCall?: never; functionResponse?: never; thoughtSignature?: string }
  | { functionCall: { name: string; args: Record<string, unknown> }; thoughtSignature?: string; text?: never; functionResponse?: never }
  | { functionResponse: { name: string; response: Record<string, unknown> }; text?: never; functionCall?: never; thoughtSignature?: never };

/** A conversation turn (user or model) with structured content parts. */
export interface AiContent {
  role: "user" | "model";
  parts: AiContentPart[];
}

/** Request to the callWithTools method. */
export interface ToolTurnRequest {
  contents: AiContent[];
  tools: AiToolDecl[];
  systemInstruction?: string;
}

/** Single-turn result from callWithTools. */
export interface ToolTurn {
  /** Joined text from all text parts in the response. */
  text: string;
  /** All functionCall parts emitted in this response turn. */
  toolCalls: AiToolCall[];
}

// ── Request / response ────────────────────────────────────────────────────────

export interface AiGenerateRequest {
  /** Conversation turns, or a single user message. */
  contents: AiMessage[];
  /** Optional generation controls (temperature, maxOutputTokens, etc.). */
  generationConfig?: Record<string, unknown>;
  /** Tool definitions for function-calling (Phase 2.5). */
  tools?: AiToolDef[];
  /** JSON schema object; when set, provider uses structured-output mode. */
  responseSchema?: Record<string, unknown>;
}

export interface AiConnectionResult {
  ok: boolean;
  error?: string;
  /** Which model actually answered (primary or fallback). */
  modelUsed?: string;
}

// ── Error type ────────────────────────────────────────────────────────────────

export type AiErrorCode =
  | "unauthorized"   // 401 — session invalid / user not signed in
  | "forbidden"      // 403 — uid not in allowlist
  | "rate_limited"   // 429 — per-user rate limit exceeded
  | "server_error"   // 5xx from proxy or upstream
  | "network_error"  // fetch threw / no connectivity
  | "parse_error";   // unexpected response shape

export class AiError extends Error {
  readonly code: AiErrorCode;
  readonly status?: number;

  constructor(code: AiErrorCode, message: string, status?: number) {
    super(message);
    this.name = "AiError";
    this.code = code;
    this.status = status;
  }
}

// ── Client interface ──────────────────────────────────────────────────────────

/**
 * Provider-agnostic AI client.
 * All Phase 2.x features call this interface; the concrete impl is wired at
 * the app root via createProxyClient (proxy-provider.ts).
 */
export interface AiClient {
  /**
   * Send a request expecting a JSON response.
   * Provider sets responseMimeType=application/json and the given schema.
   * Throws AiError on failure.
   */
  generateJson<T>(req: AiGenerateRequest): Promise<T>;

  /**
   * Send a request expecting a plain-text response.
   * Throws AiError on failure.
   */
  generateText(req: AiGenerateRequest): Promise<string>;

  /**
   * Ping the proxy with a tiny prompt to verify auth + CORS + model access.
   * Never throws; returns { ok, error?, modelUsed? }.
   */
  testConnection(): Promise<AiConnectionResult>;

  /**
   * Send a function-calling turn request to the model.
   * Parses the response into text parts + functionCall parts.
   * Throws AiError on failure.
   */
  callWithTools(req: ToolTurnRequest): Promise<ToolTurn>;
}
